// src/screens/AsambleasScreen.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebaseConfig';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { 
  IoCalendarOutline, 
  IoTimeOutline, 
  IoVideocamOutline, 
  IoDocumentTextOutline, 
  IoPeopleOutline, 
  IoListOutline, 
  IoChevronDown, 
  IoChevronUp,
  IoSettingsOutline,
  IoCheckmarkCircleOutline,
  IoClose,
  IoCopyOutline,
  IoStatsChartOutline,
  IoCheckmark
} from 'react-icons/io5';
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';
import KondoSidebar from '../components/Shared/KondoSidebar';
import KondoBottomNav from '../components/Shared/KondoBottomNav';
import { downloadIcsCalendar } from '../utils/calendarUtils';
import './AsambleasScreen.css';

function AsambleasScreen() {
  const navigate = useNavigate();
  const { userData, currentUser } = useAuth();
  const [asambleas, setAsambleas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrdenId, setExpandedOrdenId] = useState(null);
  const [activeTab, setActiveTab] = useState('proximas'); // 'proximas' | 'concluidas'
  const [asambleaParaVerMinuta, setAsambleaParaVerMinuta] = useState(null);
  const [copiado, setCopiado] = useState(false);

  const condoId = userData?.condominioId;
  const isAdmin = ['admin', 'comite', 'superuser', 'masteruser'].includes((userData?.rol || '').toLowerCase());

  useEffect(() => {
    if (!condoId) return;

    const q = query(
      collection(db, 'asambleas'),
      where('condominioId', '==', condoId),
      orderBy('fechaHora1', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAsambleas(list);
      setLoading(false);
    }, (error) => {
      console.warn("Error cargando asambleas:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [condoId]);

  const toggleOrden = (id) => {
    setExpandedOrdenId(expandedOrdenId === id ? null : id);
  };

  const handleDownloadCalendar = (asamblea) => {
    let startDateVal = asamblea.fechaHora1;
    if (!startDateVal && asamblea.fecha) {
      startDateVal = `${asamblea.fecha}T${asamblea.hora1 || '19:00'}:00`;
    }

    downloadIcsCalendar({
      title: asamblea.titulo || 'Asamblea General de Condóminos',
      description: `Asamblea de condóminos Kondify.\nOrden del día:\n${(asamblea.ordenDelDia || []).map((p, i) => `${i + 1}. ${p}`).join('\n')}`,
      startDate: startDateVal || new Date(),
      location: 'Sala Virtual Kondify'
    });
  };

  const asambleasEnVivo = asambleas.filter(a => a.estatus === 'en_vivo');
  const asambleasProximas = asambleas.filter(a => a.estatus === 'programada');
  const asambleasConcluidas = asambleas.filter(a => a.estatus === 'concluida');

  return (
    <div className="ks-page">
      <KondoSidebar activeRoute={isAdmin ? "/admin/votaciones" : "/votaciones"} />

      <main className="ks-main">
        <div className="ks-header-sticky-wrapper">
          <KondoBrandHeader showBack={true} />
        </div>

        <div className="ks-scroll-area">
          {/* Header Banner */}
          <div className="muro-header-stitch">
            <div className="muro-hero-card">
              <div className="muro-hero-content">
                <h1>Asambleas Virtuales</h1>
                <p>Juntas de condóminos, quórum legal y streaming en vivo</p>
              </div>
              <IoVideocamOutline className="muro-hero-icon-decor" />
            </div>
          </div>

          <div className="asambleas-container">
            {/* Barra superior con botón de gestión para Admin */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => setActiveTab('proximas')}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '10px',
                    border: 'none',
                    fontWeight: '700',
                    fontSize: '0.85rem',
                    cursor: 'pointer',
                    background: activeTab === 'proximas' ? '#7E66FC' : '#E2E8F0',
                    color: activeTab === 'proximas' ? '#FFFFFF' : '#475569',
                    transition: 'all 0.2s'
                  }}
                >
                  En Vivo & Próximas ({asambleasEnVivo.length + asambleasProximas.length})
                </button>
                <button
                  onClick={() => setActiveTab('concluidas')}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '10px',
                    border: 'none',
                    fontWeight: '700',
                    fontSize: '0.85rem',
                    cursor: 'pointer',
                    background: activeTab === 'concluidas' ? '#7E66FC' : '#E2E8F0',
                    color: activeTab === 'concluidas' ? '#FFFFFF' : '#475569',
                    transition: 'all 0.2s'
                  }}
                >
                  Concluidas ({asambleasConcluidas.length})
                </button>
              </div>

              {isAdmin && (
                <button
                  onClick={() => navigate('/admin/asambleas')}
                  style={{
                    background: '#0F172A',
                    color: '#FFFFFF',
                    border: 'none',
                    borderRadius: '10px',
                    padding: '8px 16px',
                    fontSize: '0.85rem',
                    fontWeight: '700',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <IoSettingsOutline size={16} />
                  <span>Panel de Convocatoria</span>
                </button>
              )}
            </div>

            {/* Listado de Asambleas */}
            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#64748B' }}>
                Cargando asambleas del condominio...
              </div>
            ) : (
              <>
                {activeTab === 'proximas' && (
                  <>
                    {/* Alerta de Asamblea En Vivo */}
                    {asambleasEnVivo.map(asamblea => (
                      <div key={asamblea.id} className="asamblea-card" style={{ border: '2px solid #DC2626' }}>
                        <div className="asamblea-header" style={{ background: '#FEF2F2' }}>
                          <span className={`asamblea-tipo-badge ${asamblea.tipo || 'ordinaria'}`}>
                            {asamblea.tipo === 'extraordinaria' ? 'Asamblea Extraordinaria' : 'Asamblea Ordinaria'}
                          </span>
                          <span className="asamblea-status-badge en_vivo">
                            🔴 EN VIVO AHORA
                          </span>
                        </div>
                        <div className="asamblea-body">
                          <h2 className="asamblea-title">{asamblea.titulo}</h2>
                          <div className="asamblea-meta-row">
                            <span className="asamblea-meta-item">
                              <IoTimeOutline size={16} />
                              Inició: {new Date(asamblea.fechaHora1 || asamblea.fecha).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                            <span className="asamblea-meta-item">
                              <IoPeopleOutline size={16} />
                              Quórum actual: {asamblea.quorumActual || 0}%
                            </span>
                          </div>
                          <p style={{ fontSize: '0.9rem', color: '#475569', margin: '0 0 16px' }}>
                            {asamblea.descripcion || 'La mesa directiva se encuentra transmitiendo en este momento.'}
                          </p>

                          <button
                            className="btn-asamblea-join"
                            onClick={() => navigate(`/asambleas/${asamblea.id}/sala`)}
                          >
                            <IoVideocamOutline size={20} />
                            <span>Entrar a la Sala en Vivo</span>
                          </button>
                        </div>
                      </div>
                    ))}

                    {/* Asambleas Programadas */}
                    {asambleasProximas.length === 0 && asambleasEnVivo.length === 0 && (
                      <div style={{
                        background: 'white',
                        borderRadius: '16px',
                        padding: '40px 20px',
                        textAlign: 'center',
                        border: '1px dashed #CBD5E1',
                        color: '#64748B'
                      }}>
                        <IoCalendarOutline size={48} style={{ color: '#94A3B8', marginBottom: '12px' }} />
                        <h3 style={{ margin: '0 0 6px', color: '#1E293B', fontWeight: '700' }}>No hay asambleas programadas</h3>
                        <p style={{ margin: 0, fontSize: '0.85rem' }}>Te notificaremos cuando la administración convoque a una nueva junta.</p>
                      </div>
                    )}

                    {asambleasProximas.map(asamblea => (
                      <div key={asamblea.id} className="asamblea-card">
                        <div className="asamblea-header">
                          <span className={`asamblea-tipo-badge ${asamblea.tipo || 'ordinaria'}`}>
                            {asamblea.tipo === 'extraordinaria' ? 'Asamblea Extraordinaria' : 'Asamblea Ordinaria'}
                          </span>
                          <span className="asamblea-status-badge programada">
                            Programada
                          </span>
                        </div>
                        <div className="asamblea-body">
                          <h2 className="asamblea-title">{asamblea.titulo}</h2>
                          
                          <div className="asamblea-meta-row">
                            <span className="asamblea-meta-item">
                              <IoCalendarOutline size={16} />
                              {new Date(asamblea.fechaHora1 || asamblea.fecha).toLocaleDateString([], { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                            </span>
                            <span className="asamblea-meta-item">
                              <IoTimeOutline size={16} />
                              1ª Conv: {asamblea.hora1 || '19:00'} | 2ª Conv: {asamblea.hora2 || '19:30'}
                            </span>
                          </div>

                          {/* Orden del Día Desplegable */}
                          {asamblea.ordenDelDia && asamblea.ordenDelDia.length > 0 && (
                            <div className="asamblea-orden-box">
                              <div 
                                className="asamblea-orden-title" 
                                style={{ cursor: 'pointer' }}
                                onClick={() => toggleOrden(asamblea.id)}
                              >
                                <IoListOutline size={16} />
                                <span>Orden del Día ({asamblea.ordenDelDia.length} puntos)</span>
                                {expandedOrdenId === asamblea.id ? <IoChevronUp size={16} /> : <IoChevronDown size={16} />}
                              </div>

                              {expandedOrdenId === asamblea.id && (
                                <div style={{ marginTop: '8px' }}>
                                  {asamblea.ordenDelDia.map((punto, idx) => (
                                    <div key={idx} className="asamblea-orden-item">
                                      <span style={{ fontWeight: '700', color: '#7E66FC' }}>{idx + 1}.</span>
                                      <span>{punto}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}

                          <div className="asamblea-actions">
                            <button
                              className="btn-asamblea-cal"
                              onClick={() => handleDownloadCalendar(asamblea)}
                            >
                              <IoCalendarOutline size={16} />
                              <span>Agregar a mi Calendario (.ics)</span>
                            </button>

                            {isAdmin && (
                              <button
                                className="btn-asamblea-join"
                                style={{ background: '#0F172A', boxShadow: 'none' }}
                                onClick={() => navigate(`/asambleas/${asamblea.id}/sala`)}
                              >
                                <IoVideocamOutline size={18} />
                                <span>Probar Sala Virtual</span>
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                )}

                {activeTab === 'concluidas' && (
                  <>
                    {asambleasConcluidas.length === 0 ? (
                      <div style={{
                        background: 'white',
                        borderRadius: '16px',
                        padding: '40px 20px',
                        textAlign: 'center',
                        border: '1px dashed #CBD5E1',
                        color: '#64748B'
                      }}>
                        <IoCheckmarkCircleOutline size={48} style={{ color: '#94A3B8', marginBottom: '12px' }} />
                        <h3 style={{ margin: '0 0 6px', color: '#1E293B', fontWeight: '700' }}>No hay asambleas concluidas aún</h3>
                        <p style={{ margin: 0, fontSize: '0.85rem' }}>Las actas y minutas aprobadas se almacenarán aquí de por vida.</p>
                      </div>
                    ) : (
                      asambleasConcluidas.map(asamblea => (
                        <div key={asamblea.id} className="asamblea-card">
                          <div className="asamblea-header">
                            <span className={`asamblea-tipo-badge ${asamblea.tipo || 'ordinaria'}`}>
                              {asamblea.tipo === 'extraordinaria' ? 'Asamblea Extraordinaria' : 'Asamblea Ordinaria'}
                            </span>
                            <span className="asamblea-status-badge concluida">
                              Concluida
                            </span>
                          </div>
                          <div className="asamblea-body">
                            <h2 className="asamblea-title">{asamblea.titulo}</h2>
                            <div className="asamblea-meta-row">
                              <span className="asamblea-meta-item">
                                <IoCalendarOutline size={16} />
                                Celebrada el: {new Date(asamblea.fechaHora1 || asamblea.fecha).toLocaleDateString()}
                              </span>
                              <span className="asamblea-meta-item">
                                <IoPeopleOutline size={16} />
                                Quórum final: {asamblea.quorumFinal || asamblea.quorumActual || 0}%
                              </span>
                            </div>

                            {asamblea.minutaUrl || asamblea.minutaTexto || (asamblea.votacionesHistorial && asamblea.votacionesHistorial.length > 0) ? (
                              <div style={{ marginTop: '14px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                <button
                                  className="btn-asamblea-minuta"
                                  onClick={() => setAsambleaParaVerMinuta(asamblea)}
                                  style={{ background: '#0F172A', color: 'white', border: 'none', borderRadius: '12px', padding: '10px 18px', fontWeight: '800', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                                >
                                  <IoDocumentTextOutline size={18} />
                                  <span>📜 Ver Minuta y Votaciones</span>
                                </button>
                                {asamblea.minutaUrl && (
                                  <button
                                    onClick={() => window.open(asamblea.minutaUrl, '_blank')}
                                    style={{ background: '#F1F5F9', color: '#475569', border: '1px solid #CBD5E1', borderRadius: '12px', padding: '10px 16px', fontWeight: '700', cursor: 'pointer' }}
                                  >
                                    Descargar PDF
                                  </button>
                                )}
                              </div>
                            ) : (
                              <p style={{ fontSize: '0.85rem', color: '#64748B', fontStyle: 'italic', margin: '8px 0 0' }}>
                                Minuta y registro de votaciones en proceso de protocolización.
                              </p>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>

        {/* MODAL OFICIAL: Minuta Notarial y Desglose de Votaciones */}
        {asambleaParaVerMinuta && (
          <div style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.85)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '16px'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '24px',
              maxWidth: '700px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              color: '#0F172A',
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.3)',
              padding: '24px'
            }}>
              {/* Modal Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                <div>
                  <span style={{ background: '#F1F5F9', color: '#64748B', padding: '4px 10px', borderRadius: '8px', fontSize: '0.75rem', fontWeight: '800' }}>
                    ACTA Y REGISTRO OFICIAL
                  </span>
                  <h2 style={{ margin: '6px 0 2px', fontSize: '1.25rem', fontWeight: '800' }}>
                    {asambleaParaVerMinuta.titulo}
                  </h2>
                  <span style={{ fontSize: '0.8rem', color: '#64748B' }}>
                    Celebrada: {new Date(asambleaParaVerMinuta.fechaHora1 || asambleaParaVerMinuta.fecha).toLocaleDateString()} • Quórum Legal: {asambleaParaVerMinuta.quorumFinal || asambleaParaVerMinuta.quorumActual || 0}%
                  </span>
                </div>
                <button
                  onClick={() => setAsambleaParaVerMinuta(null)}
                  style={{ background: '#F1F5F9', border: 'none', borderRadius: '50%', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                >
                  <IoClose size={20} />
                </button>
              </div>

              {/* Sección de Votaciones Digitales Registradas */}
              <div style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '16px', padding: '16px', marginBottom: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                  <IoStatsChartOutline size={20} color="#7E66FC" />
                  <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '800' }}>
                    Votaciones Digitales Registradas en Vivo
                  </h3>
                </div>

                {asambleaParaVerMinuta.votacionesHistorial && asambleaParaVerMinuta.votacionesHistorial.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {asambleaParaVerMinuta.votacionesHistorial.map((v, i) => {
                      const favor = v.votosAFavor || 0;
                      const contra = v.votosEnContra || 0;
                      const abst = v.votosAbstencion || 0;
                      const total = favor + contra + abst;
                      const aprobado = favor > contra;

                      return (
                        <div key={i} style={{ background: 'white', padding: '12px 16px', borderRadius: '12px', border: '1px solid #E2E8F0', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px', marginBottom: '8px' }}>
                            <strong style={{ fontSize: '0.88rem', color: '#1E293B' }}>{v.pregunta}</strong>
                            <span style={{
                              background: aprobado ? '#DCFCE7' : '#FEE2E2',
                              color: aprobado ? '#15803D' : '#B91C1C',
                              padding: '2px 8px',
                              borderRadius: '6px',
                              fontSize: '0.72rem',
                              fontWeight: '800',
                              whiteSpace: 'nowrap'
                            }}>
                              {aprobado ? '✓ APROBADO' : '✗ RECHAZADO'}
                            </span>
                          </div>

                          <div style={{ display: 'flex', gap: '16px', fontSize: '0.8rem', color: '#64748B' }}>
                            <span style={{ color: '#059669', fontWeight: '700' }}>👍 {favor} A Favor</span>
                            <span style={{ color: '#DC2626', fontWeight: '700' }}>👎 {contra} En Contra</span>
                            <span>✋ {abst} Abstención</span>
                            <span style={{ marginLeft: 'auto', fontWeight: '700', color: '#475569' }}>Total: {total} votos</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p style={{ margin: 0, fontSize: '0.82rem', color: '#64748B', fontStyle: 'italic' }}>
                    {asambleaParaVerMinuta.minutaTexto ? "Las votaciones se encuentran detalladas en el acta inferior." : "No se registraron votaciones formales durante esta asamblea."}
                  </p>
                )}
              </div>

              {/* Sección de Minuta Notarial Escrita */}
              <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '800' }}>
                    Minuta Notarial Certificada
                  </h3>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(asambleaParaVerMinuta.minutaTexto || '');
                      setCopiado(true);
                      setTimeout(() => setCopiado(false), 2000);
                    }}
                    style={{ background: '#F1F5F9', border: 'none', borderRadius: '8px', padding: '6px 12px', fontSize: '0.75rem', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
                  >
                    {copiado ? <IoCheckmark size={14} color="#16A34A" /> : <IoCopyOutline size={14} />}
                    <span>{copiado ? '¡Copiado!' : 'Copiar Acta'}</span>
                  </button>
                </div>

                <div style={{
                  background: '#F8FAFC',
                  border: '1px solid #E2E8F0',
                  borderRadius: '12px',
                  padding: '16px',
                  fontFamily: 'monospace',
                  fontSize: '0.8rem',
                  lineHeight: '1.6',
                  whiteSpace: 'pre-wrap',
                  maxHeight: '300px',
                  overflowY: 'auto'
                }}>
                  {asambleaParaVerMinuta.minutaTexto || 'Minuta en proceso de firma y protocolización.'}
                </div>
              </div>

              {/* Botón de Cerrar */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button
                  onClick={() => setAsambleaParaVerMinuta(null)}
                  style={{ background: '#0F172A', color: 'white', border: 'none', borderRadius: '12px', padding: '10px 22px', fontWeight: '800', cursor: 'pointer' }}
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="ks-bottom-bar">
          <KondoBottomNav activeRoute={isAdmin ? "/admin/votaciones" : "/votaciones"} />
        </div>
      </main>
    </div>
  );
}

export default AsambleasScreen;
